import type { ClientAnswer, SurveyResponse } from '@/api/orderDetails'
import {
  extractBaseSurveyQuestionCode,
  getDisplayNameForCode,
} from '@/utils/surveyQuestionDisplayNames'

type Locale = 'en' | 'ar'

type UnknownRecord = Record<string, unknown>

interface SnapshotQuestionMeta {
  id: string
  code: string
  questionType: string
  order: number
  textEn?: string
  textAr?: string
  optionLabelsByValue: Map<string, { en?: string; ar?: string }>
}

function asRecord(value: unknown): UnknownRecord | null {
  if (typeof value !== 'object' || value == null || Array.isArray(value)) return null
  return value as UnknownRecord
}

function parseUnknownObject(value: unknown): UnknownRecord | null {
  if (value == null) return null
  if (typeof value === 'string') {
    try {
      return asRecord(JSON.parse(value) as unknown)
    } catch {
      return null
    }
  }
  return asRecord(value)
}

function parseQuestionOptionLabel(
  labelValue: UnknownRecord | null,
  fallbackLabel: unknown,
  locale: Locale
): string | undefined {
  const fromTranslations =
    locale === 'ar'
      ? (labelValue?.ar as string | undefined) ?? (labelValue?.en as string | undefined)
      : (labelValue?.en as string | undefined) ?? (labelValue?.ar as string | undefined)
  if (typeof fromTranslations === 'string' && fromTranslations.trim() !== '') return fromTranslations.trim()
  if (typeof fallbackLabel === 'string' && fallbackLabel.trim() !== '') return fallbackLabel.trim()
  return undefined
}

function normalizeSnapshotQuestions(snapshotData: unknown, locale: Locale): SnapshotQuestionMeta[] {
  const snapshotObj = parseUnknownObject(snapshotData)
  if (!snapshotObj) return []

  const questions = Array.isArray(snapshotObj.questions) ? snapshotObj.questions : []
  const normalized: SnapshotQuestionMeta[] = []

  for (let index = 0; index < questions.length; index += 1) {
    const rawQuestion = asRecord(questions[index])
    if (!rawQuestion) continue

    const id = typeof rawQuestion.id === 'string' ? rawQuestion.id : `question-${index + 1}`
    const code = typeof rawQuestion.code === 'string' ? rawQuestion.code : id
    const questionType =
      typeof rawQuestion.question_type === 'string'
        ? rawQuestion.question_type
        : typeof rawQuestion.questionType === 'string'
          ? rawQuestion.questionType
          : 'text'
    const order =
      typeof rawQuestion.order === 'number' && Number.isFinite(rawQuestion.order)
        ? rawQuestion.order
        : index + 1

    const textTranslations = asRecord(rawQuestion.text_translations)
    const textEn =
      (typeof textTranslations?.en === 'string' && textTranslations.en.trim() !== ''
        ? textTranslations.en.trim()
        : undefined) ??
      (typeof rawQuestion.textEn === 'string' && rawQuestion.textEn.trim() !== ''
        ? rawQuestion.textEn.trim()
        : undefined)
    const textAr =
      (typeof textTranslations?.ar === 'string' && textTranslations.ar.trim() !== ''
        ? textTranslations.ar.trim()
        : undefined) ??
      (typeof rawQuestion.textAr === 'string' && rawQuestion.textAr.trim() !== ''
        ? rawQuestion.textAr.trim()
        : undefined)

    const optionLabelsByValue = new Map<string, { en?: string; ar?: string }>()
    const options = Array.isArray(rawQuestion.options) ? rawQuestion.options : []
    for (const option of options) {
      const rawOption = asRecord(option)
      if (!rawOption) continue
      const optionValue =
        typeof rawOption.value === 'string'
          ? rawOption.value.trim().toLowerCase()
          : typeof rawOption.value === 'number'
            ? String(rawOption.value)
            : ''
      if (!optionValue) continue

      const labelTranslations = asRecord(rawOption.label_translations)
      const labelEn = parseQuestionOptionLabel(labelTranslations, rawOption.labelEn ?? rawOption.label, 'en')
      const labelAr = parseQuestionOptionLabel(labelTranslations, rawOption.labelAr ?? rawOption.label, 'ar')
      const localizedLabel = parseQuestionOptionLabel(labelTranslations, rawOption.label, locale)
      optionLabelsByValue.set(optionValue, {
        en: labelEn ?? localizedLabel,
        ar: labelAr ?? localizedLabel,
      })
    }

    normalized.push({
      id,
      code,
      questionType,
      order,
      textEn,
      textAr,
      optionLabelsByValue,
    })
  }

  return normalized.sort((a, b) => a.order - b.order)
}

function getSnapshotQuestionDisplayLabel(question: SnapshotQuestionMeta, locale: Locale): string {
  if (locale === 'ar') return question.textAr ?? question.textEn ?? question.code
  return question.textEn ?? question.textAr ?? question.code
}

function normalizeAnswerToDisplayString(
  value: unknown,
  question: SnapshotQuestionMeta | undefined,
  locale: Locale
): string {
  const mapOptionLabel = (primitiveValue: string): string => {
    const option = question?.optionLabelsByValue.get(primitiveValue.trim().toLowerCase())
    const localized = locale === 'ar' ? option?.ar ?? option?.en : option?.en ?? option?.ar
    return localized ?? primitiveValue
  }

  if (value == null) return ''
  if (typeof value === 'string') return mapOptionLabel(value.trim())
  if (typeof value === 'number') return String(value)
  if (typeof value === 'boolean') return value ? (locale === 'ar' ? 'نعم' : 'Yes') : locale === 'ar' ? 'لا' : 'No'
  if (Array.isArray(value)) {
    if (value.length === 0) return ''
    const primitiveValues = value.filter(
      (item) => typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean'
    ) as Array<string | number | boolean>
    if (primitiveValues.length === value.length) {
      return primitiveValues
        .map((item) =>
          typeof item === 'boolean'
            ? item
              ? locale === 'ar'
                ? 'نعم'
                : 'Yes'
              : locale === 'ar'
                ? 'لا'
                : 'No'
            : mapOptionLabel(String(item).trim())
        )
        .join(', ')
    }
    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }

  const objectValue = asRecord(value)
  if (objectValue) {
    const candidate = objectValue.answer ?? objectValue.value ?? objectValue.label ?? objectValue.text
    if (typeof candidate === 'string') return mapOptionLabel(candidate.trim())
    if (typeof candidate === 'number') return String(candidate)
    if (typeof candidate === 'boolean') return candidate ? (locale === 'ar' ? 'نعم' : 'Yes') : locale === 'ar' ? 'لا' : 'No'
    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }

  return String(value)
}

function keyHasValue(record: UnknownRecord, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key)
}

function normalizeSurveyAnswerLookupKey(key: string): string {
  const trimmed = key.trim().toLowerCase()
  const withoutIndexSuffix = trimmed.replace(/_(\d+)$/, '')
  const baseCode = extractBaseSurveyQuestionCode(withoutIndexSuffix)
  return (baseCode ?? withoutIndexSuffix).trim().toLowerCase()
}

function buildSurveyAnswerLookupAliases(answersRecord: UnknownRecord): Map<string, string> {
  const lookup = new Map<string, string>()
  for (const key of Object.keys(answersRecord)) {
    const aliases = [key.trim().toLowerCase(), normalizeSurveyAnswerLookupKey(key)]
    for (const alias of aliases) {
      if (!alias || lookup.has(alias)) continue
      lookup.set(alias, key)
    }
  }
  return lookup
}

function isAnsweredSurveyValue(value: unknown): boolean {
  if (value == null) return false
  if (typeof value === 'string') return value.trim() !== ''
  if (typeof value === 'number' || typeof value === 'boolean') return true
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'object') {
    const obj = asRecord(value)
    if (!obj) return false
    return Object.values(obj).some((entry) => isAnsweredSurveyValue(entry))
  }
  return false
}

export function getSurveyResponseTitle(response: SurveyResponse, locale: Locale): string {
  return (
    response.surveySnapshot?.survey?.title ||
    (locale === 'ar'
      ? response.surveySnapshot?.survey?.titleAr
      : response.surveySnapshot?.survey?.titleEn) ||
    (locale === 'ar' ? 'استبيان' : 'Survey')
  )
}

export type SurveyResponseBucket = 'provider' | 'client' | 'protocol' | 'other'

export function classifySurveyResponse(response: SurveyResponse, locale: Locale): SurveyResponseBucket {
  const title = getSurveyResponseTitle(response, locale).toLowerCase()
  if (
    /protocol|before|during|after|بروتوكول|قبل|أثناء|بعد|pre-?service|post-?service|in-?service/i.test(
      title
    )
  ) {
    return 'protocol'
  }
  if (/provider|مقدم|caregiver|staff/i.test(title) && /feedback|ملاحظ|تقييم|rating|review/i.test(title)) {
    return 'provider'
  }
  if (/provider|مقدم/i.test(title)) {
    return 'provider'
  }
  if (/client|عميل|customer|beneficiary|مستفيد/i.test(title) && /feedback|ملاحظ|تقييم|rating|review/i.test(title)) {
    return 'client'
  }
  if (/client|عميل|customer|beneficiary|مستفيد/i.test(title)) {
    return 'client'
  }
  return 'other'
}

export function bucketSurveyResponses(
  responses: SurveyResponse[],
  locale: Locale
): Record<SurveyResponseBucket, SurveyResponse[]> {
  const buckets: Record<SurveyResponseBucket, SurveyResponse[]> = {
    provider: [],
    client: [],
    protocol: [],
    other: [],
  }
  for (const response of responses) {
    buckets[classifySurveyResponse(response, locale)].push(response)
  }
  return buckets
}

export function getResponseCompletionPercentage(response: SurveyResponse): number {
  const apiCompletion =
    typeof response.completionPercentage === 'number' && Number.isFinite(response.completionPercentage)
      ? response.completionPercentage
      : 0
  const clampedApi = Math.max(0, Math.min(100, apiCompletion))
  if (clampedApi > 0) return clampedApi
  if (!response.submitted) return clampedApi

  const answersRecord = parseUnknownObject(response.answers)
  if (!answersRecord || Object.keys(answersRecord).length === 0) return 100

  const snapshotQuestions = normalizeSnapshotQuestions(response.surveySnapshot?.snapshotData, 'en')
  if (snapshotQuestions.length === 0) return 100

  const byAlias = buildSurveyAnswerLookupAliases(answersRecord)

  let answeredCount = 0
  for (const question of snapshotQuestions) {
    const matchedKey = keyHasValue(answersRecord, question.id)
      ? question.id
      : keyHasValue(answersRecord, question.code)
        ? question.code
        : byAlias.get(normalizeSurveyAnswerLookupKey(question.id)) ??
          byAlias.get(normalizeSurveyAnswerLookupKey(question.code)) ??
          null
    if (!matchedKey) continue
    if (isAnsweredSurveyValue(answersRecord[matchedKey])) answeredCount += 1
  }

  if (answeredCount === 0) {
    const anyAnswered = Object.keys(answersRecord).some((key) =>
      isAnsweredSurveyValue(answersRecord[key])
    )
    return anyAnswered ? 100 : 0
  }

  return Math.round((answeredCount / snapshotQuestions.length) * 100)
}

export function mapSubmittedResponseAnswersToClientAnswers(
  response: SurveyResponse,
  locale: Locale
): ClientAnswer[] {
  const answersRecord = parseUnknownObject(response.answers)
  if (!answersRecord) return []

  const snapshotQuestions = normalizeSnapshotQuestions(response.surveySnapshot?.snapshotData, locale)
  const lookupByAlias = buildSurveyAnswerLookupAliases(answersRecord)

  const usedKeys = new Set<string>()
  const mappedAnswers: ClientAnswer[] = []

  const takeAnswer = (candidate: string | undefined): { key: string; value: unknown } | null => {
    if (!candidate || candidate.trim() === '') return null
    const exactKey = keyHasValue(answersRecord, candidate) ? candidate : null
    const resolvedKey =
      exactKey ??
      lookupByAlias.get(candidate.trim().toLowerCase()) ??
      lookupByAlias.get(normalizeSurveyAnswerLookupKey(candidate)) ??
      null
    if (!resolvedKey || usedKeys.has(resolvedKey)) return null
    usedKeys.add(resolvedKey)
    return { key: resolvedKey, value: answersRecord[resolvedKey] }
  }

  for (const question of snapshotQuestions) {
    const matched = takeAnswer(question.id) ?? takeAnswer(question.code)
    if (!matched) continue
    mappedAnswers.push({
      questionText: getSnapshotQuestionDisplayLabel(question, locale),
      answer: normalizeAnswerToDisplayString(matched.value, question, locale),
      questionType: question.questionType,
      questionCode: question.code,
      rawAnswer: matched.value,
      instanceNumber: null,
      instanceLabel: null,
      scope: 'submitted_response',
      templateQuestionId: question.id,
      questionBankId: null,
    })
  }

  for (const key of Object.keys(answersRecord)) {
    if (usedKeys.has(key)) continue
    const value = answersRecord[key]
    const normalizedCode = extractBaseSurveyQuestionCode(key) ?? key
    const fallbackLabel = getDisplayNameForCode(normalizedCode, locale) ?? key
    mappedAnswers.push({
      questionText: fallbackLabel,
      answer: normalizeAnswerToDisplayString(value, undefined, locale),
      questionType: 'text',
      questionCode: normalizedCode,
      rawAnswer: value,
      instanceNumber: null,
      instanceLabel: null,
      scope: 'submitted_response',
      templateQuestionId: key,
      questionBankId: null,
    })
  }

  return mappedAnswers
}
